import {Alert, ButtonGroup, DataStatus, Radio} from 'platform/components';
import {Hide, VStack} from 'platform/foundation';

import {useState} from 'react';
import {useSelector} from 'react-redux';

import {isNotNil} from 'ramda';

import {useGetBranchListQuery} from '@dms/api/core';
import {selectUserBranchOptions} from '@dms/api/features';
import {BranchPreselectionRecordType, useGetBranchPreselectionQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

export interface CreationBranchDialogProps extends TestIdProps {
  recordType: BranchPreselectionRecordType;
  isVehicleBranchInaccessible: boolean;
  selectedBranchId: string;
  vehicleBranchId: string;
  onConfirm: (branchId: string) => void;
  onCancel: () => void;
}

export function CreationBranchDialog(props: CreationBranchDialogProps) {
  const [chosenBranchId, setChosenBranchId] = useState<string | null>(null);
  const userBranchOptions = useSelector(selectUserBranchOptions) ?? [];

  const preselection = useGetBranchPreselectionQuery(
    {recordType: props.recordType},
    {skip: props.isVehicleBranchInaccessible}
  );
  // Names of branches the user cannot access are only known from the tenant branch list
  const branchList = useGetBranchListQuery(undefined, {
    skip: !props.isVehicleBranchInaccessible,
  });

  // A failed setting read falls back to the selected branch; the setting is a preference only
  const preselectedBranchId =
    preselection.data?.branchPreselection === 'VEHICLE_BRANCH'
      ? props.vehicleBranchId
      : props.selectedBranchId;
  const branchId = props.isVehicleBranchInaccessible
    ? props.selectedBranchId
    : (chosenBranchId ?? preselectedBranchId);

  const getBranchName = (id: string) =>
    userBranchOptions.find((option) => option.value === id)?.label ??
    branchList.data?.branchListItems.find((branch) => branch.id === id)?.marketingName ??
    i18n.t('entity.settings.labels.unknownBranch');

  const branchNames = {
    selectedBranch: getBranchName(props.selectedBranchId),
    vehicleBranch: getBranchName(props.vehicleBranchId),
  };

  const options = [
    {
      label: i18n.t('entity.branch.creationBranch.selectedBranchOption', {
        branch: branchNames.selectedBranch,
      }),
      value: props.selectedBranchId,
    },
    {
      label: i18n.t('entity.branch.creationBranch.vehicleBranchOption', {
        branch: branchNames.vehicleBranch,
      }),
      value: props.vehicleBranchId,
    },
  ];

  return (
    <DataStatus isLoading={preselection.isLoading || branchList.isLoading} minHeight={30}>
      <VStack spacing={4}>
        <Alert
          variant="warning"
          message={
            props.isVehicleBranchInaccessible
              ? i18n.t('entity.branch.creationBranch.inaccessibleDescription', branchNames)
              : i18n.t('entity.branch.creationBranch.mismatchDescription', branchNames)
          }
          data-testid={suffixTestId('alert', props)}
        />
        <Hide when={props.isVehicleBranchInaccessible}>
          <Radio
            direction="column"
            spacing={2}
            value={branchId}
            onChange={(value) => {
              if (isNotNil(value)) {
                setChosenBranchId(value);
              }
            }}
            options={options}
            data-testid={suffixTestId('branch', props)}
          />
        </Hide>
        <ButtonGroup
          align="right"
          buttons={[
            {
              title: i18n.t('general.actions.cancel'),
              variant: 'secondary',
              onClick: props.onCancel,
              'data-testid': suffixTestId('cancel', props),
            },
            {
              title: props.isVehicleBranchInaccessible
                ? i18n.t('general.actions.continue')
                : i18n.t('general.actions.confirm'),
              variant: 'primary',
              onClick: () => props.onConfirm(branchId),
              'data-testid': suffixTestId('confirm', props),
            },
          ]}
        />
      </VStack>
    </DataStatus>
  );
}
